import { useEffect, useRef, useState } from "react";
import { apiFetch, supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileText, Upload, Download, Trash2, Eye, Pencil } from "lucide-react";
import { formatDateTime } from "@/lib/ui";
import { logActivity } from "@/lib/activity";
import { WorkspaceCard, SectionHeader, EmptyState } from "@/components/design";

const fileExt = (n) => (n?.split(".").pop() || "").toLowerCase();
const formatSize = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const ALLOWED_EXT = ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg", "png", "csv", "txt"];
const BLOCKED_EXT = ["exe", "bat", "cmd", "sh", "com", "scr", "msi", "app", "dmg", "js", "vbs", "ps1", "jar"];

/**
 * DocumentPanel — uploads directly to Supabase storage (private "documents" bucket)
 * and inserts a `documents` row, matching legacy Documents.jsx behavior.
 * Downloads go through backend `/api/documents/{id}/download` (which does exist).
 */
export default function DocumentPanel({ client, recordId, onSaved, onContext }) {
  const { currentOrgId, user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const [active, setActive] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = async () => {
    if (!client) { setDocs([]); return; }
    setLoading(true);
    const { data } = await supabase.from("documents")
      .select("id,name,size_bytes,mime_type,updated_at,created_at,client_id,storage_path,document_type")
      .eq("organization_id", currentOrgId)
      .eq("client_id", client.id)
      .order("updated_at", { ascending: false });
    setDocs(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client?.id, currentOrgId]);

  useEffect(() => {
    if (!recordId) { setActive(null); setPreviewUrl(null); return; }
    supabase.from("documents").select("*, clients(first_name,last_name)")
      .eq("id", recordId).limit(1).maybeSingle().then(({ data }) => setActive(data || null));
  }, [recordId]);

  // Auto-fetch signed preview URL when a document is selected.
  useEffect(() => {
    if (!active) { setPreviewUrl(null); return; }
    setPreviewLoading(true);
    apiFetch(`/documents/${active.id}/download`).then((r) => setPreviewUrl(r.url))
      .catch(() => setPreviewUrl(null))
      .finally(() => setPreviewLoading(false));
  }, [active?.id]);

  const doUpload = async (file) => {
    if (!file) return;
    if (!client) return toast.error("Pick a client first");
    const ext = fileExt(file.name);
    if (BLOCKED_EXT.includes(ext)) return toast.error(`.${ext} files are not allowed for security reasons`);
    if (!ALLOWED_EXT.includes(ext)) return toast.error(`Unsupported file type. Allowed: ${ALLOWED_EXT.join(", ").toUpperCase()}`);
    if (file.size > 25 * 1024 * 1024) return toast.error("File too large (max 25 MB)");
    setUploading(true);
    try {
      const path = `orgs/${currentOrgId}/clients/${client.id}/${crypto.randomUUID()}-${file.name}`;
      const up = await supabase.storage.from("documents").upload(path, file, { contentType: file.type });
      if (up.error) throw up.error;
      const { data, error } = await supabase.from("documents").insert({
        organization_id: currentOrgId, client_id: client.id,
        name: file.name, document_type: "other",
        storage_path: path, mime_type: file.type, size_bytes: file.size,
        uploaded_by: user?.id,
      }).select().single();
      if (error) throw error;
      toast.success("Document uploaded");
      logActivity({ organization_id: currentOrgId, actor_user_id: user?.id, client_id: client.id,
        action: "document.uploaded", summary: `uploaded ${file.name}`,
        entity_type: "documents", entity_id: data.id }).catch(() => {});
      onSaved && onSaved({ kind: "document", id: data.id });
      load();
    } catch (e) { toast.error(e.message || "Upload failed"); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const download = async (id) => {
    try {
      const r = await apiFetch(`/documents/${id}/download`);
      window.open(r.url, "_blank");
    } catch (e) { toast.error("Download failed"); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.name}"? The file will be removed from storage and cannot be recovered.`)) return;
    try {
      // Backend endpoint handles storage cleanup + audit log + org-membership check.
      await apiFetch(`/documents/${d.id}`, { method: "DELETE" });
      toast.success("Deleted");
      if (active?.id === d.id) setActive(null);
      load();
      // Signal deletion so the outer FormsDocuments drawer closes.
      onSaved && onSaved({ kind: "document", id: d.id, deleted: true });
    } catch (e) { toast.error(e.message); }
  };

  const rename = async (d) => {
    const next = window.prompt("Rename document to:", d.name);
    if (!next || !next.trim() || next.trim() === d.name) return;
    try {
      const r = await apiFetch(`/documents/${d.id}`, {
        method: "PATCH",
        body: JSON.stringify({ name: next.trim() }),
      });
      if (!r || !r.id) return toast.error("Rename was blocked. You may not have permission.");
      toast.success("Renamed");
      setActive((a) => (a && a.id === d.id ? { ...a, name: r.name, updated_at: r.updated_at } : a));
      load();
      // Refresh outer FormsDocuments records list so the parent shows the new name.
      onSaved && onSaved({ kind: "document", id: d.id });
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    onContext && onContext({
      client, editing: active,
      actions: active ? [
        { key: "dl", label: "Download", variant: "outline", testid: "doc-download", onClick: () => download(active.id) },
        { key: "rn", label: "Rename", icon: Pencil, variant: "outline", testid: "doc-rename", onClick: () => rename(active) },
        { key: "rm", label: "Delete", icon: Trash2, variant: "outline", testid: "doc-delete", onClick: () => remove(active) },
      ] : [
        { key: "upload", label: uploading ? "Uploading…" : "Upload document", variant: "primary", testid: "doc-upload", onClick: () => inputRef.current?.click(), disabled: uploading || !client },
      ],
    });
    /* eslint-disable-next-line */
  }, [client?.id, active, uploading]);

  const renderInlinePreview = () => {
    if (previewLoading || !previewUrl) {
      return <div className="text-center py-20 text-sm text-[#57534E]">Loading preview…</div>;
    }
    const ext = fileExt(active.name);
    if (["jpg", "jpeg", "png"].includes(ext)) {
      return <img src={previewUrl} alt={active.name} className="w-full h-auto max-h-[60vh] object-contain mx-auto" />;
    }
    if (ext === "pdf") {
      return <iframe src={previewUrl} title={active.name} className="w-full bg-white" style={{ height: "60vh", border: 0 }} />;
    }
    if (["txt", "csv"].includes(ext)) {
      return <iframe src={previewUrl} title={active.name} className="w-full bg-white" style={{ height: "60vh", border: 0 }} />;
    }
    return (
      <div className="text-center py-16">
        <FileText size={40} className="mx-auto text-[#A8A29E] mb-3" strokeWidth={1.5} />
        <p className="text-sm text-[#57534E]">This file type can't be previewed inline.</p>
        <p className="text-xs text-[#A8A29E] mt-1">Use <b>Download</b> to open it in a new tab.</p>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="workspace-document">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#57534E] font-semibold flex items-center gap-1.5"><FileText size={13} /> Documents</div>
        <h2 className="text-lg font-display font-semibold tracking-tight">{active ? active.name : (client ? `${client.first_name} ${client.last_name}` : "Pick a client")}</h2>
        {active && <div className="text-xs text-[#57534E] mt-0.5"><span className="uppercase">{fileExt(active.name)}</span> · {formatSize(active.size_bytes)} · Uploaded {formatDateTime(active.created_at)}</div>}
      </div>

      <input ref={inputRef} type="file" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} data-testid="doc-file-input" />

      {/* Preview or upload prompt */}
      {active ? (
        <WorkspaceCard className="p-0 overflow-hidden">
          <div className="bg-[#FAF8F5] rounded-t-2xl" style={{ minHeight: "50vh" }} data-testid="doc-inline-preview">
            {renderInlinePreview()}
          </div>
        </WorkspaceCard>
      ) : (
        <EmptyState
          icon={Upload}
          title={client ? "No document selected" : "Choose a client first"}
          description={client ? `Attach SOPs, brand assets, agreements or any client file. Allowed: ${ALLOWED_EXT.join(", ")} · Max 25 MB` : "Pick a client on the left, then upload their documents here."}
          action={
            <Button className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]" disabled={uploading || !client} onClick={() => inputRef.current?.click()} data-testid="doc-choose-file">
              <Upload size={13} className="mr-1.5" /> {uploading ? "Uploading…" : "Upload document"}
            </Button>
          }
          testid="doc-empty"
        />
      )}

      <WorkspaceCard className="p-0">
        <div className="px-4 py-3 border-b border-[#E8E2D9] flex items-center justify-between">
          <SectionHeader title={client ? "Files for this client" : "Select a client to see files"} />
          {client && (
            <button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="doc-upload-more"
              className="text-xs text-[#E8712C] hover:underline flex items-center gap-1">
              <Upload size={11} /> Upload
            </button>
          )}
        </div>
        {loading ? (
          <div className="p-6 text-center text-sm text-[#57534E]">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="p-6 text-center text-sm text-[#A8A29E]">No documents yet.</div>
        ) : (
          <div className="divide-y divide-[#E8E2D9] max-h-64 overflow-y-auto">
            {docs.map((d) => (
              <div key={d.id} onClick={() => setActive(d)}
                className={`px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-[#FAF8F5] ${active?.id === d.id ? "bg-[#FDE9D8]" : ""}`}
                data-testid={`doc-row-${d.id}`}>
                <FileText size={16} className="text-[#57534E] flex-shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{d.name}</div>
                  <div className="text-xs text-[#57534E]"><span className="uppercase">{fileExt(d.name)}</span> · {formatSize(d.size_bytes)} · {formatDateTime(d.updated_at || d.created_at)}</div>
                </div>
                <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={(e) => { e.stopPropagation(); setActive(d); }} data-testid={`doc-view-${d.id}`} title="Preview"><Eye size={13} /></Button>
                <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={(e) => { e.stopPropagation(); download(d.id); }} data-testid={`doc-dl-${d.id}`} title="Download"><Download size={13} /></Button>
                <Button size="sm" variant="ghost" className="rounded-full h-8 text-[#B02222]" onClick={(e) => { e.stopPropagation(); remove(d); }} data-testid={`doc-rm-${d.id}`} title="Delete"><Trash2 size={13} /></Button>
              </div>
            ))}
          </div>
        )}
      </WorkspaceCard>
    </div>
  );
}
